import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useBrandContext } from "@/contexts/brand-context";
import { useAccounts } from "@/hooks/use-accounts";
import { useInboxComments } from "@/hooks/use-inbox-comments";
import { CommentCard } from "@/components/inbox/comment-card";
import { InboxFilters, type InboxStatusFilter } from "@/components/inbox/inbox-filters";
import { INBOX_PLATFORMS } from "@content-os/config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export function InboxCommentsPage() {
  const { t } = useTranslation("inbox");
  const { brandSlug, commentId } = useParams<{ brandSlug: string; commentId?: string }>();
  const { brand } = useBrandContext();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<InboxStatusFilter>("OPEN");
  const [channel, setChannel] = useState<string>("");
  const query = useInboxComments({
    brandId: brand.id,
    status: statusFilter,
    platform: channel || undefined,
  });
  const { data: accounts } = useAccounts(brand.id);
  const hasActiveAccount = (accounts ?? []).some(
    (a) => a.status === "ACTIVE" && (INBOX_PLATFORMS as string[]).includes(a.platform),
  );
  const comments = useMemo(
    () => query.data?.pages.flatMap((p) => p.comments) ?? [],
    [query.data],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border px-4 py-3 flex-shrink-0">
        <h3 className="text-[14px] font-semibold">{t("commentsPage.title")}</h3>
        <p className="text-[11px] text-muted-foreground">{t("commentsPage.subtitle")}</p>
      </div>
      <InboxFilters
        status={statusFilter}
        onStatusChange={setStatusFilter}
        channel={channel}
        onChannelChange={setChannel}
      />
      <div className="flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="flex flex-col gap-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <MessageCircle size={32} className="mb-3 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">{t("commentsPage.empty")}</p>
            {!hasActiveAccount && (
              <Link
                to={`/${brandSlug}/settings/accounts`}
                className="mt-2 text-xs text-brand hover:underline"
              >
                {t("commentsPage.connectAccount")}
              </Link>
            )}
          </div>
        ) : (
          <>
            {comments.map((c) => (
              <CommentCard
                key={c.id}
                comment={c}
                selected={commentId === c.id}
                onSelect={() => navigate(`/${brandSlug}/inbox/comments/${c.id}`)}
              />
            ))}
            {query.hasNextPage && (
              <div className="p-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={query.isFetchingNextPage}
                  onClick={() => query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? t("commentsPage.loading") : t("commentsPage.loadMore")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
